/**
 * Os quadros do rastreio e da previsão que estão na memória, por sistema — o
 * que a ficha e a trajetória consultam para acompanhar o player: os números do
 * sistema no horário exibido, a história mais longa que se conhece dele, a
 * trajetória passada e a previsão a partir daquele horário.
 *
 * A camada (`layer.ts`) registra cada quadro que baixa e esquece os que
 * descarta; `versao` no store avisa a ficha de que algo mudou.
 */
import type { Coordinate } from 'ol/coordinate';
import type Feature from 'ol/Feature';
import LineString from 'ol/geom/LineString';
import Polygon from 'ol/geom/Polygon';
import { lonLatToMap, mapToLonLat } from '../map/projection';
import type { Sistema } from './layer';
import { TOLERANCIA_MS } from './tempo';

/** Quem é o sistema entre um quadro e outro: limiar, `uid` e, no núcleo, `iuid`. */
export function chaveDoSistema(s: Pick<Sistema, 'lim' | 'uid' | 'iuid'>): string {
  return `${s.lim ?? ''}|${s.uid ?? ''}|${s.iuid ?? ''}`;
}

/**
 * Do contorno observado de um sistema num quadro: o que emenda a trajetória
 * prevista na passada e o que já está sob ele (as cidades de "agora").
 */
export interface Ancora {
  /** A trajetória passada (`k:"t"`, projeção do mapa), da mais antiga ao quadro; null sem ela. */
  trajeto: Coordinate[] | null;
  /** Ponto dentro do contorno observado (projeção do mapa). */
  dentro: [number, number];
  geometria: Polygon;
}

/** Uma antecedência da previsão de um sistema. */
export interface PassoPrevisto {
  /** Antecedência (min). */
  h: number;
  /**
   * Onde o sistema estará (projeção do mapa): o fim da trajetória passada
   * deslocado do mesmo tanto que o contorno previsto se deslocou do observado
   * — assim a linha prevista emenda na passada sem degrau. Sem o observado,
   * o ponto dentro do contorno previsto.
   */
  centro: [number, number];
  geometria: Polygon;
}

/** A previsão de um sistema a partir de um quadro. `passos` vazio: o arquivo chegou, e não tem o sistema. */
export interface PrevisaoDoSistema {
  /** Horário do quadro de origem (epoch ms). */
  t: number;
  passos: PassoPrevisto[];
}

interface Previsto {
  h: number;
  dentro: [number, number];
  geometria: Polygon;
}

/** Por horário do quadro (epoch ms): os sistemas observados dele. */
const quadros = new Map<number, Map<string, Sistema>>();
/** Por horário do quadro: a âncora de cada sistema observado. */
const ancoras = new Map<number, Map<string, Ancora>>();
/** Por horário do quadro de origem: os contornos previstos de cada sistema. */
const previsoes = new Map<number, Map<string, Previsto[]>>();
let aoMudar: (() => void) | null = null;

export function aoMudarRegistro(fn: (() => void) | null): void {
  aoMudar = fn;
}

const dentroDe = (g: Polygon): [number, number] => {
  const p = g.getInteriorPoint().getCoordinates();
  return [p[0], p[1]];
};

/**
 * As âncoras de um quadro observado: o contorno de cada sistema (sem `uid`,
 * não há como achá-lo de novo) e a trajetória dele, que o arquivo traz por
 * `uid` e limiar (`k:"t"`).
 */
export function ancorasDoQuadro(feicoes: readonly Feature[]): Map<string, Ancora> {
  const trajetos = new Map<string, Coordinate[]>();
  for (const f of feicoes) {
    const g = f.getGeometry();
    if (f.get('k') !== 't' || !(g instanceof LineString)) continue;
    const uid = f.get('uid') as unknown;
    const lim = f.get('lim') as unknown;
    if (typeof uid !== 'number') continue;
    trajetos.set(chaveDoSistema({ uid, lim: typeof lim === 'number' ? lim : null, iuid: null }), g.getCoordinates());
  }
  const out = new Map<string, Ancora>();
  for (const f of feicoes) {
    const s = f.get('sistema') as Sistema | undefined;
    const g = f.getGeometry();
    if (!s || s.previsto || s.uid === null || !(g instanceof Polygon)) continue;
    const chave = chaveDoSistema(s);
    const trajeto = trajetos.get(chave);
    out.set(chave, { trajeto: trajeto && trajeto.length ? trajeto : null, dentro: dentroDe(g), geometria: g });
  }
  return out;
}

export function registrarQuadro(t: number, sistemas: readonly Sistema[], doQuadro: Map<string, Ancora> = new Map()): void {
  const mapa = new Map<string, Sistema>();
  for (const s of sistemas) if (!s.previsto) mapa.set(chaveDoSistema(s), s);
  quadros.set(t, mapa);
  ancoras.set(t, doQuadro);
  aoMudar?.();
}

export function esquecerQuadro(t: number): void {
  const a = quadros.delete(t);
  const b = ancoras.delete(t);
  if (a || b) aoMudar?.();
}

/** Um arquivo de previsão entrou na memória: os contornos previstos de cada sistema, por antecedência. */
export function registrarPrevisao(t: number, feicoes: readonly Feature[]): void {
  const mapa = new Map<string, Previsto[]>();
  for (const f of feicoes) {
    const s = f.get('sistema') as Sistema | undefined;
    const g = f.getGeometry();
    if (!s || !s.previsto || s.uid === null || s.h === null || s.h <= 0 || !(g instanceof Polygon)) continue;
    const chave = chaveDoSistema(s);
    const lista = mapa.get(chave) ?? [];
    // Uma por antecedência: um arquivo com o mesmo `h` repetido fica com o primeiro.
    if (!lista.some((p) => p.h === s.h)) lista.push({ h: s.h, dentro: dentroDe(g), geometria: g });
    mapa.set(chave, lista);
  }
  for (const lista of mapa.values()) lista.sort((a, b) => a.h - b.h);
  previsoes.set(t, mapa);
  aoMudar?.();
}

export function esquecerPrevisao(t: number): void {
  if (previsoes.delete(t)) aoMudar?.();
}

export function limparRegistro(): void {
  if (!quadros.size && !ancoras.size && !previsoes.size) return;
  quadros.clear();
  ancoras.clear();
  previsoes.clear();
  aoMudar?.();
}

/** A entrada do instante `t`, com a tolerância do casamento no tempo. */
function doInstante<V>(mapa: Map<number, V>, t: number | null): [number, V] | null {
  if (t === null) return null;
  const exato = mapa.get(t);
  if (exato !== undefined) return [t, exato];
  for (const [tq, v] of mapa) if (Math.abs(tq - t) <= TOLERANCIA_MS) return [tq, v];
  return null;
}

/** O sistema `chave` no quadro do instante `t` (com a tolerância do casamento no tempo), ou null. */
export function sistemaEm(chave: string, t: number | null): Sistema | null {
  return doInstante(quadros, t)?.[1].get(chave) ?? null;
}

/** O registro mais novo que se tem do sistema `chave`: a história dele é a mais longa. */
export function ultimoRegistro(chave: string): Sistema | null {
  let melhor: Sistema | null = null;
  for (const [t, mapa] of quadros) {
    const s = mapa.get(chave);
    if (s && (!melhor || t > melhor.t)) melhor = s;
  }
  return melhor;
}

/** O contorno observado e a trajetória do sistema `chave` no quadro do instante `t`, ou null. */
export function ancoraDoSistema(chave: string, t: number | null): Ancora | null {
  return doInstante(ancoras, t)?.[1].get(chave) ?? null;
}

/**
 * A previsão do sistema `chave` a partir do quadro do instante `t`: null
 * enquanto o arquivo não está na memória; `passos` vazio se ele chegou sem o
 * sistema.
 */
export function previsaoDoSistema(chave: string, t: number | null): PrevisaoDoSistema | null {
  const achado = doInstante(previsoes, t);
  if (!achado) return null;
  const [tq, mapa] = achado;
  const lista = mapa.get(chave) ?? [];
  const ancora = ancoraDoSistema(chave, tq);
  const base = ancora ? (ancora.trajeto?.[ancora.trajeto.length - 1] ?? ancora.dentro) : null;
  return {
    t: tq,
    passos: lista.map((p) => ({
      h: p.h,
      centro: ancora && base ? [base[0] + p.dentro[0] - ancora.dentro[0], base[1] + p.dentro[1] - ancora.dentro[1]] : p.dentro,
      geometria: p.geometria,
    })),
  };
}

/** Raio médio da Terra (km), para o ponto de destino. */
const RAIO_DA_TERRA_KM = 6371;

/** O ponto a `km` de (lon, lat) no rumo `rumo` (graus a partir do norte), na esfera. */
export function destino(lon: number, lat: number, rumo: number, km: number): [number, number] {
  const d = km / RAIO_DA_TERRA_KM;
  const t = (rumo * Math.PI) / 180;
  const f1 = (lat * Math.PI) / 180;
  const l1 = (lon * Math.PI) / 180;
  const f2 = Math.asin(Math.sin(f1) * Math.cos(d) + Math.cos(f1) * Math.sin(d) * Math.cos(t));
  const l2 = l1 + Math.atan2(Math.sin(t) * Math.sin(d) * Math.cos(f1), Math.cos(d) - Math.sin(f1) * Math.sin(f2));
  return [(l2 * 180) / Math.PI, (f2 * 180) / Math.PI];
}

/** Os centros previstos de um sistema: a antecedência (min) e onde o centro estará (projeção do mapa). */
export interface CentrosPrevistos {
  /** Horário do quadro de origem (epoch ms). */
  t: number;
  passos: { h: number; centro: [number, number] }[];
}

/** Acima disso (km/h) o vetor é erro de associação do rastreio (fusão, divisão), não movimento: sem cone. */
export const VELOCIDADE_MAXIMA_KMH = 200;

/**
 * O vetor (unidades do mapa por minuto) com que a previsão publicada translada
 * o sistema `chave`, tirado do arquivo da previsão que está na memória no
 * quadro mais perto de `t`; null sem nenhum.
 */
function vetorDoArquivo(chave: string, t: number): [number, number] | null {
  let melhor: [number, number] | null = null;
  let distancia = Infinity;
  for (const [tq, mapa] of previsoes) {
    const lista = mapa.get(chave);
    const ancora = ancoraDoSistema(chave, tq);
    const ultimo = lista?.[lista.length - 1];
    if (!ultimo || !ancora || !(ultimo.h > 0) || Math.abs(tq - t) >= distancia) continue;
    distancia = Math.abs(tq - t);
    melhor = [(ultimo.dentro[0] - ancora.dentro[0]) / ultimo.h, (ultimo.dentro[1] - ancora.dentro[1]) / ultimo.h];
  }
  return melhor;
}

/**
 * Os centros da previsão do sistema `chave` no quadro do instante `t` sem o
 * arquivo daquele quadro — para o cone com o player tocando (09/10/2026: o
 * cone acompanha o sistema na animação), quando os arquivos da previsão não
 * são baixados. O fim da trajetória (ou o ponto dentro do contorno) é levado:
 * - pelo vetor do arquivo da previsão mais perto no tempo que está na memória
 *   (o da ficha aberta, ao menos): o MESMO movimento do cone parado, sem salto
 *   ao pausar. A velocidade publicada no quadro sai 20–40 % maior que a da
 *   translação publicada (medido em 09/10/2026), e o cone pularia;
 * - sem nenhum arquivo, pela velocidade e pelo rumo do próprio quadro.
 * Null sem o sistema, sem âncora, sem vetor ou com vetor implausível
 * (`VELOCIDADE_MAXIMA_KMH`).
 */
export function previsaoPeloVetor(chave: string, t: number | null, antecedencias: readonly number[]): CentrosPrevistos | null {
  const achado = doInstante(quadros, t);
  if (!achado || !antecedencias.length) return null;
  const [tq, mapa] = achado;
  const s = mapa.get(chave);
  const ancora = ancoraDoSistema(chave, tq);
  if (!s || !ancora) return null;
  const base = ancora.trajeto?.[ancora.trajeto.length - 1] ?? ancora.dentro;
  const v = vetorDoArquivo(chave, tq);
  if (v) {
    // Unidades do mapa por minuto → km/h (a projeção é em metros).
    if (Math.hypot(v[0], v[1]) * 0.06 > VELOCIDADE_MAXIMA_KMH) return null;
    return { t: tq, passos: antecedencias.map((h) => ({ h, centro: [base[0] + v[0] * h, base[1] + v[1] * h] })) };
  }
  if (s.vel === null || s.rumo === null || !Number.isFinite(s.vel) || !Number.isFinite(s.rumo) || s.vel > VELOCIDADE_MAXIMA_KMH) return null;
  const [lon, lat] = mapToLonLat(base[0], base[1]);
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
  const passos: CentrosPrevistos['passos'] = [];
  for (const h of antecedencias) {
    const centro = lonLatToMap(...destino(lon, lat, s.rumo, (s.vel * h) / 60));
    if (Number.isFinite(centro[0]) && Number.isFinite(centro[1])) passos.push({ h, centro });
  }
  return passos.length ? { t: tq, passos } : null;
}
